import { useCallback, useEffect, useState } from 'react';
import type { DigestDto } from '@epasu/core';
import {
  crm,
  type CustomerRow,
  type FollowupRow,
  type MailRow,
  type PipelineMetaRow,
  type ProjectRow,
  type TaskRow,
} from '@/lib/crm-client';

/** Wie viele Zeilen eine Liste auf dem Dashboard höchstens zeigt. */
export const ZEILEN = 6;

export interface DashboardData {
  digest: DigestDto;
  /** Offene Aufgaben, die bis heute fällig sind — dringendste zuerst. */
  tasks: TaskRow[];
  /**
   * Antworten auf Erstansprachen, die noch keine Einordnung (Positiv /
   * Abgesagt) haben, neueste zuerst. Vollständig, nicht auf die gezeigten
   * Zeilen begrenzt — die Karte zählt daraus „und n weitere“.
   */
  antworten: MailRow[];
  /** Erstansprachen, bei denen ein Nachfassen ansteht. */
  followups: FollowupRow[];
  customers: CustomerRow[];
  pipelines: PipelineMetaRow[];
  /** Laufende Projekte — Abgeschlossenes und Abgebrochenes bleibt draußen. */
  projects: ProjectRow[];
}

/**
 * Alles, was das Dashboard anzeigt, in einem Zug geladen.
 *
 * Der Digest trägt die Seite: schlägt er fehl, gibt es nichts zu zeigen. Die
 * fünf Listen daneben fallen einzeln auf „leer“ zurück, statt die ganze Seite
 * mitzureißen — ein nicht verbundener Kalender oder ein Postfach mit Schluckauf
 * darf nicht dazu führen, dass auch die fälligen Aufgaben verschwinden.
 *
 * `reload` lädt neu, nachdem ein Schnellzugriff etwas angelegt hat.
 */
export function useDashboardData() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [durchlauf, setDurchlauf] = useState(0);

  const reload = useCallback(() => setDurchlauf((n) => n + 1), []);

  useEffect(() => {
    let abgebrochen = false;

    void (async () => {
      try {
        const [digest, tasks, antworten, followups, customers, pipelines, projects] =
          await Promise.all([
            crm.system.digest(),
            weich(crm.tasks.list({ limit: 100 })),
            weich(
              // Dieselbe Abfrage wie die Antwortliste unter /leads/antworten:
              // nur Antworten auf eine Kampagnenmail, `assigned` wie im
              // Posteingang. Die Einordnung (`replySentiment`) filtert die
              // Abfrage nicht mit — das geschieht unten.
              crm.mailbox.list({ onlyOutreachReplies: true }),
            ),
            weich(crm.followups.list({ states: ['faellig'], limit: ZEILEN })),
            weich(crm.customers.list()),
            weich(crm.pipelines.listWithMeta()),
            weich(crm.projects.list()),
          ]);
        if (abgebrochen) return;
        setError(null);
        setData({
          digest,
          tasks: bisHeuteFaellig(tasks),
          antworten: antworten.filter((m) => m.replySentiment === null),
          followups,
          customers,
          pipelines,
          projects,
        });
      } catch (err) {
        if (abgebrochen) return;
        setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
      }
    })();

    return () => {
      abgebrochen = true;
    };
  }, [durchlauf]);

  return { data, error, reload };
}

/** Eine Liste, deren Fehlschlag die Seite nicht kostet. */
async function weich<T>(p: Promise<T[]>): Promise<T[]> {
  try {
    return await p;
  } catch {
    return [];
  }
}

/**
 * Die Aufgaben, die heute dran sind. Undatierte bleiben draußen: sie sind
 * nicht dringend, nur unbestimmt — und stünden sonst zwischen dem, was drängt.
 *
 * Die Reihenfolge kommt schon richtig aus der API (Fälligkeit aufsteigend),
 * also wird hier nur gefiltert.
 */
function bisHeuteFaellig(tasks: TaskRow[]): TaskRow[] {
  const ende = new Date();
  ende.setHours(23, 59, 59, 999);
  const grenze = ende.toISOString();
  return tasks.filter((t) => t.dueAt !== null && t.dueAt <= grenze);
}

/** Heute, 0 Uhr — davor ist überfällig. */
export function heuteBeginn(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}
